import { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';

/**
 * Three.js Neural Atlas 3D Brain Component
 * 
 * Features:
 * - Cerebrum: Dual-hemisphere low-poly geodesic dome with longitudinal fissure
 * - Cerebellum: Dual posterior lower lobes
 * - Brainstem: Descending central neural column (pons / medulla)
 * - Outer Constellation Web: Outer satellite nodes and radiating axon lines
 * - Transparent wireframe lines + glowing diamond vertex nodes
 * - Traveling action potential impulse sparks
 * - Interactive 3D floating landmark beacons (projected 3D to 2D screen coordinates)
 * - Drag-to-rotate orbital controls with inertia & damping
 */

const BEACONS_CONFIG = [
  {
    id: 'beacon-neurosyn',
    targetId: 'dossier-neurosyn',
    label: '01 • NEUROSYN',
    sub: 'iOS HealthKit Engine',
    // Frontal-left cortex region
    anchorCoords: [-0.65, 0.45, 0.65]
  },
  {
    id: 'beacon-lyrika',
    targetId: 'dossier-lyrika',
    label: '02 • LYRIKA-STANZA',
    sub: 'Live Lyric Daemon',
    // Temporal-right cortex region
    anchorCoords: [0.85, -0.05, 0.2]
  },
  {
    id: 'beacon-tamed',
    targetId: 'dossier-tamed',
    label: '03 • TAMED',
    sub: 'Full-Stack XP Engine',
    // Occipital / posterior-left region
    anchorCoords: [-0.7, -0.2, -0.65]
  },
  {
    id: 'beacon-research',
    targetId: 'dossier-research',
    label: '04 • FORGETTING PARADOX',
    sub: 'Neuro x ML Synthesis',
    // Superior dorsal parietal region
    anchorCoords: [0.3, 0.85, -0.15]
  },
  {
    id: 'beacon-stem',
    targetId: 'dossier-neurosyn',
    label: '05 • BRAINSTEM // NUCLEI',
    sub: 'Central Nervous Core',
    // Brainstem region
    anchorCoords: [0.0, -0.85, -0.08]
  }
];

const NeuralBrain = ({ className = '', onBeaconClick }) => {
  const containerRef = useRef(null);
  const [beaconPositions, setBeaconPositions] = useState([]);
  const [isInteracting, setIsInteracting] = useState(false);

  // Handle beacon click to smoothly scroll to target section
  const handleBeaconClick = useCallback((targetId) => {
    if (onBeaconClick) {
      onBeaconClick(targetId);
      return;
    }
    const elem = document.getElementById(targetId);
    if (elem) {
      elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      elem.classList.add('ring-2', 'ring-purple-400', 'shadow-[0_0_40px_rgba(192,132,252,0.35)]');
      setTimeout(() => {
        elem.classList.remove('ring-2', 'ring-purple-400', 'shadow-[0_0_40px_rgba(192,132,252,0.35)]');
      }, 2000);
    }
  }, [onBeaconClick]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let width = container.clientWidth || window.innerWidth;
    let height = container.clientHeight || (typeof window !== 'undefined' ? window.innerHeight : 620);
    const isMobile = width < 768;

    // --- Scene, Camera, Renderer Setup ---
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x050409, 0.038);

    const aspect = width / height;
    const fov = isMobile ? (aspect < 0.6 ? 56 : 50) : 40;
    const camera = new THREE.PerspectiveCamera(fov, aspect, 0.1, 100);
    camera.position.set(0, isMobile ? 0.3 : 0.15, isMobile ? 6.2 : 5.4);

    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance'
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    // Root Group & Framing Position
    let basePosY = isMobile ? 0.38 : (aspect < 1.4 ? 0.28 : 0.22);
    const brainGroup = new THREE.Group();
    brainGroup.position.y = basePosY;
    scene.add(brainGroup);

    // Node & Line Data Collections
    const allPositions = [];
    const currentPositions = [];
    const allColors = [];
    const connections = [];

    // Colors matching reference image (deep neon violet, electric purple, magenta, hot pink, soft rose)
    const colorPurple = new THREE.Color(0xa855f7);
    const colorViolet = new THREE.Color(0xc084fc);
    const colorMagenta = new THREE.Color(0xe879f9);
    const colorDeepPurple = new THREE.Color(0x7c3aed);
    const colorRose = new THREE.Color(0xf43f5e);
    const colorPink = new THREE.Color(0xf472b6);

    // 1. --- CEREBRUM GEOMETRY (Dual Hemispheres) ---
    const CEREBRUM_COUNT = 920;
    const cerebrumBaseIndices = [];

    for (let i = 0; i < CEREBRUM_COUNT; i++) {
      const isLeft = i % 2 === 0;
      const hemisphereSign = isLeft ? -1 : 1;

      const u = Math.random();
      const v = Math.random();
      const theta = u * 2.0 * Math.PI;
      const phi = Math.acos(2.0 * v - 1.0);

      let sx = Math.sin(phi) * Math.cos(theta);
      let sy = Math.sin(phi) * Math.sin(theta);
      let sz = Math.cos(phi);

      // Low-poly hemisphere shaping
      let bx = sx * 1.08;
      let by = sy * 0.94;
      let bz = sz * 1.28;

      // Longitudinal fissure gap
      const fissureGap = 0.15;
      bx = hemisphereSign * (Math.abs(bx) * 0.94 + fissureGap);

      // Frontal expansion
      if (bz > 0) {
        bx *= 1.06;
        by *= 1.02;
      }
      // Occipital rounding
      if (bz < 0) {
        by *= 0.92;
      }
      // Temporal lateral bulge
      if (by < 0.15 && Math.abs(bz) < 0.6) {
        bx *= 1.14;
      }

      // Sulci / Gyri wave folds
      const fold = Math.sin(6.0 * phi) * Math.cos(6.0 * theta);
      const radiusMod = 1.0 + 0.11 * fold + (Math.random() - 0.5) * 0.06;
      const depthLayer = 0.72 + 0.28 * Math.pow(Math.random(), 0.5);
      const scale = 1.32 * radiusMod * depthLayer;

      const px = bx * scale;
      const py = by * scale + 0.12;
      const pz = bz * scale;

      const idx = allPositions.length / 3;
      cerebrumBaseIndices.push(idx);
      allPositions.push(px, py, pz);
      currentPositions.push(px, py, pz);

      const col = new THREE.Color().lerpColors(
        colorDeepPurple,
        colorViolet,
        Math.random() * 0.8 + 0.2
      );
      if (bz > 0.3) col.lerp(colorMagenta, 0.45);
      allColors.push(col.r, col.g, col.b);
    }

    // 2. --- CEREBELLUM GEOMETRY (Dual Posterior Lobes) ---
    const CEREBELLUM_COUNT = 240;
    const cerebellumBaseIndices = [];

    for (let i = 0; i < CEREBELLUM_COUNT; i++) {
      const isLeft = i % 2 === 0;
      const lobeSign = isLeft ? -1 : 1;

      // Small sphere cluster around (±0.42, -0.42, -0.52)
      const u = Math.random();
      const v = Math.random();
      const theta = u * 2.0 * Math.PI;
      const phi = Math.acos(2.0 * v - 1.0);

      const r = 0.38 * (0.8 + 0.2 * Math.random());
      const cx = lobeSign * 0.44 + r * Math.sin(phi) * Math.cos(theta) * 1.05;
      const cy = -0.42 + r * Math.sin(phi) * Math.sin(theta) * 0.82;
      const cz = -0.52 + r * Math.cos(phi) * 0.95;

      const idx = allPositions.length / 3;
      cerebellumBaseIndices.push(idx);
      allPositions.push(cx, cy, cz);
      currentPositions.push(cx, cy, cz);

      const col = new THREE.Color().lerpColors(colorPurple, colorMagenta, Math.random() * 0.7);
      allColors.push(col.r, col.g, col.b);
    }

    // 3. --- BRAINSTEM GEOMETRY (Descending Central Column) ---
    const BRAINSTEM_COUNT = 160;
    const stemBaseIndices = [];
    const stemLayers = 14;
    const nodesPerLayer = Math.floor(BRAINSTEM_COUNT / stemLayers);

    for (let l = 0; l < stemLayers; l++) {
      const t = l / (stemLayers - 1); // 0 (top/pons) to 1 (bottom/medulla)
      const y = -0.28 - t * 0.82; // from y = -0.28 down to y = -1.10
      const radius = THREE.MathUtils.lerp(0.24, 0.11, t); // Tapers downwards
      const zOffset = THREE.MathUtils.lerp(-0.15, -0.06, t); // Curves slightly forward

      for (let n = 0; n < nodesPerLayer; n++) {
        const angle = (n / nodesPerLayer) * Math.PI * 2 + (l % 2) * 0.25;
        const x = Math.cos(angle) * radius * (0.85 + Math.random() * 0.3);
        const z = zOffset + Math.sin(angle) * radius * (0.85 + Math.random() * 0.3);
        const py = y + (Math.random() - 0.5) * 0.04;

        const idx = allPositions.length / 3;
        stemBaseIndices.push(idx);
        allPositions.push(x, py, z);
        currentPositions.push(x, py, z);

        // Gradient from violet at the top to bright magenta/rose at the stem tip
        const col = new THREE.Color().lerpColors(
          colorViolet,
          colorMagenta,
          t * 0.8 + 0.2 * Math.random()
        );
        if (t > 0.7) col.lerp(colorRose, 0.45);
        allColors.push(col.r, col.g, col.b);
      }
    }

    // 4. --- OUTER CONSTELLATION WEB (Radiating Satellite Nodes & Rays) ---
    const CONSTELLATION_COUNT = 150;
    const constellationIndices = [];

    for (let i = 0; i < CONSTELLATION_COUNT; i++) {
      // Sample positions floating outside the brain
      const u = Math.random();
      const v = Math.random();
      const theta = u * 2.0 * Math.PI;
      const phi = Math.acos(2.0 * v - 1.0);

      // Distance from 1.4 to 2.35, shaped to stay beautifully within viewport
      const dist = 1.4 + Math.random() * 0.95;
      const ox = Math.sin(phi) * Math.cos(theta) * dist * 1.12;
      let oy = Math.sin(phi) * Math.sin(theta) * dist * 0.72 + 0.1;
      // Softly clamp bottom constellation rays so they don't get sliced by viewport bottom
      if (oy < -1.15) {
        oy = -1.15 + (oy + 1.15) * 0.25;
      }
      const oz = Math.cos(phi) * dist * 0.95;

      const idx = allPositions.length / 3;
      constellationIndices.push(idx);
      allPositions.push(ox, oy, oz);
      currentPositions.push(ox, oy, oz);

      // Outer satellite nodes: glowing magenta / violet
      const col = new THREE.Color().lerpColors(
        colorMagenta,
        colorViolet,
        Math.random()
      );
      allColors.push(col.r, col.g, col.b);
    }

    const TOTAL_NODES = allPositions.length / 3;

    // --- BUILD SYNAPTIC & CONSTELLATION CONNECTIONS ---
    // A. Connect Cerebrum & Cerebellum internally
    const connectGroup = (indices, maxDist, maxConns) => {
      for (let i = 0; i < indices.length; i++) {
        let count = 0;
        const idxA = indices[i];
        const pA = new THREE.Vector3(
          allPositions[idxA * 3],
          allPositions[idxA * 3 + 1],
          allPositions[idxA * 3 + 2]
        );

        const checkEnd = Math.min(indices.length, i + 65);
        for (let j = i + 1; j < checkEnd; j++) {
          const idxB = indices[j];
          const pB = new THREE.Vector3(
            allPositions[idxB * 3],
            allPositions[idxB * 3 + 1],
            allPositions[idxB * 3 + 2]
          );

          if (pA.distanceTo(pB) < maxDist) {
            connections.push(idxA, idxB);
            count++;
            if (count >= maxConns) break;
          }
        }
      }
    };

    connectGroup(cerebrumBaseIndices, 0.36, 3);
    connectGroup(cerebellumBaseIndices, 0.32, 3);

    // B. Connect Brainstem rings and vertical spine fibers
    for (let l = 0; l < stemLayers; l++) {
      const layerStart = l * nodesPerLayer;
      for (let n = 0; n < nodesPerLayer; n++) {
        const curIdx = stemBaseIndices[layerStart + n];
        const nextInRing = stemBaseIndices[layerStart + ((n + 1) % nodesPerLayer)];
        connections.push(curIdx, nextInRing);

        // Vertical connection to layer below
        if (l < stemLayers - 1) {
          const belowIdx = stemBaseIndices[(l + 1) * nodesPerLayer + n];
          connections.push(curIdx, belowIdx);

          // Diagonal lattice cross
          if (n % 2 === 0) {
            const diagIdx = stemBaseIndices[(l + 1) * nodesPerLayer + ((n + 1) % nodesPerLayer)];
            connections.push(curIdx, diagIdx);
          }
        }
      }
    }

    // Connect top of brainstem to cerebrum/cerebellum base
    for (let n = 0; n < nodesPerLayer; n++) {
      const stemTopIdx = stemBaseIndices[n];
      const pTop = new THREE.Vector3(
        allPositions[stemTopIdx * 3],
        allPositions[stemTopIdx * 3 + 1],
        allPositions[stemTopIdx * 3 + 2]
      );

      for (let i = 0; i < 40; i++) {
        const cIdx = cerebrumBaseIndices[Math.floor(Math.random() * cerebrumBaseIndices.length)];
        const pC = new THREE.Vector3(
          allPositions[cIdx * 3],
          allPositions[cIdx * 3 + 1],
          allPositions[cIdx * 3 + 2]
        );
        if (pTop.distanceTo(pC) < 0.45) {
          connections.push(stemTopIdx, cIdx);
          break;
        }
      }
    }

    // C. Connect Outer Constellation nodes to surface & each other
    for (let i = 0; i < constellationIndices.length; i++) {
      const outIdx = constellationIndices[i];
      const pOut = new THREE.Vector3(
        allPositions[outIdx * 3],
        allPositions[outIdx * 3 + 1],
        allPositions[outIdx * 3 + 2]
      );

      // Connect to nearest surface node
      let closestSurfaceIdx = -1;
      let minSurfaceDist = 999;
      for (let s = 0; s < 120; s++) {
        const randSurface = cerebrumBaseIndices[Math.floor(Math.random() * cerebrumBaseIndices.length)];
        const pSurf = new THREE.Vector3(
          allPositions[randSurface * 3],
          allPositions[randSurface * 3 + 1],
          allPositions[randSurface * 3 + 2]
        );
        const dist = pOut.distanceTo(pSurf);
        if (dist < minSurfaceDist) {
          minSurfaceDist = dist;
          closestSurfaceIdx = randSurface;
        }
      }
      if (closestSurfaceIdx !== -1 && minSurfaceDist < 1.8) {
        connections.push(outIdx, closestSurfaceIdx);
      }

      // Connect to nearby outer satellite nodes
      for (let j = i + 1; j < Math.min(constellationIndices.length, i + 12); j++) {
        const outIdx2 = constellationIndices[j];
        const pOut2 = new THREE.Vector3(
          allPositions[outIdx2 * 3],
          allPositions[outIdx2 * 3 + 1],
          allPositions[outIdx2 * 3 + 2]
        );
        if (pOut.distanceTo(pOut2) < 0.95) {
          connections.push(outIdx, outIdx2);
        }
      }
    }

    // --- THREE.LineSegments Wireframe ---
    const linePositions = new Float32Array(connections.length * 3);
    const lineColors = new Float32Array(connections.length * 3);

    for (let k = 0; k < connections.length; k++) {
      const nodeIdx = connections[k];
      linePositions[k * 3] = allPositions[nodeIdx * 3];
      linePositions[k * 3 + 1] = allPositions[nodeIdx * 3 + 1];
      linePositions[k * 3 + 2] = allPositions[nodeIdx * 3 + 2];

      lineColors[k * 3] = allColors[nodeIdx * 3];
      lineColors[k * 3 + 1] = allColors[nodeIdx * 3 + 1];
      lineColors[k * 3 + 2] = allColors[nodeIdx * 3 + 2];
    }

    const lineGeometry = new THREE.BufferGeometry();
    lineGeometry.setAttribute('position', new THREE.BufferAttribute(linePositions, 3));
    lineGeometry.setAttribute('color', new THREE.BufferAttribute(lineColors, 3));

    const lineMaterial = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.42,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });

    const lineMesh = new THREE.LineSegments(lineGeometry, lineMaterial);
    brainGroup.add(lineMesh);

    // --- THREE.Points Diamond Node Sprite ---
    const createDiamondSprite = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 64;
      canvas.height = 64;
      const ctx = canvas.getContext('2d');

      ctx.clearRect(0, 0, 64, 64);
      // Draw diamond / faceted square
      ctx.save();
      ctx.translate(32, 32);
      ctx.rotate(Math.PI / 4);

      // Outer soft glow
      ctx.shadowColor = '#e879f9';
      ctx.shadowBlur = 12;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(-12, -12, 24, 24);

      // Inner core
      ctx.fillStyle = '#fdf4ff';
      ctx.fillRect(-8, -8, 16, 16);
      ctx.restore();

      const texture = new THREE.CanvasTexture(canvas);
      texture.needsUpdate = true;
      return texture;
    };

    const diamondTexture = createDiamondSprite();

    const nodeGeometry = new THREE.BufferGeometry();
    nodeGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(currentPositions), 3));
    nodeGeometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(allColors), 3));

    const nodeMaterial = new THREE.PointsMaterial({
      size: 0.088,
      vertexColors: true,
      map: diamondTexture,
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });

    const nodePoints = new THREE.Points(nodeGeometry, nodeMaterial);
    brainGroup.add(nodePoints);

    // --- ACTION POTENTIAL TRAVELING IMPULSES ---
    const SIGNAL_COUNT = 65;
    const signals = [];
    for (let s = 0; s < SIGNAL_COUNT; s++) {
      const connIdx = Math.floor(Math.random() * (connections.length / 2)) * 2;
      signals.push({
        from: connections[connIdx],
        to: connections[connIdx + 1],
        progress: Math.random(),
        speed: 0.45 + Math.random() * 0.95,
        color: Math.random() > 0.6 ? colorMagenta : (Math.random() > 0.5 ? colorRose : colorPink)
      });
    }

    const signalPositions = new Float32Array(SIGNAL_COUNT * 3);
    const signalColors = new Float32Array(SIGNAL_COUNT * 3);
    const signalGeometry = new THREE.BufferGeometry();
    signalGeometry.setAttribute('position', new THREE.BufferAttribute(signalPositions, 3));
    signalGeometry.setAttribute('color', new THREE.BufferAttribute(signalColors, 3));

    const signalMaterial = new THREE.PointsMaterial({
      size: 0.16,
      vertexColors: true,
      map: diamondTexture,
      transparent: true,
      opacity: 1.0,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });

    const signalPoints = new THREE.Points(signalGeometry, signalMaterial);
    brainGroup.add(signalPoints);

    // Initial positioning & orientation matching reference view
    // Initial positioning & orientation matching reference view
    brainGroup.rotation.y = -0.32;
    brainGroup.rotation.x = 0.18;

    // --- INTERACTIVE DRAG-TO-ROTATE ORBIT CONTROLS (iOS OPTIMIZED) ---
    let isDragging = false;
    let prevMousePos = { x: 0, y: 0 };
    let rotationVelocity = { x: 0, y: 0 };

    const onPointerDown = (e) => {
      isDragging = true;
      setIsInteracting(true);
      const isTouch = !!e.touches;
      prevMousePos = {
        x: isTouch ? e.touches[0].clientX : e.clientX,
        y: isTouch ? e.touches[0].clientY : e.clientY
      };
    };

    const onPointerMove = (e) => {
      if (!isDragging) return;
      const isTouch = !!e.touches;
      const clientX = isTouch ? e.touches[0].clientX : e.clientX;
      const clientY = isTouch ? e.touches[0].clientY : e.clientY;

      const deltaX = clientX - prevMousePos.x;
      const deltaY = clientY - prevMousePos.y;

      // On mobile touch, if user is primarily swiping horizontally, rotate brain
      if (isTouch) {
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          rotationVelocity.y = deltaX * 0.007;
          brainGroup.rotation.y += rotationVelocity.y;
        }
      } else {
        rotationVelocity.y = deltaX * 0.006;
        rotationVelocity.x = deltaY * 0.006;
        brainGroup.rotation.y += rotationVelocity.y;
        brainGroup.rotation.x += rotationVelocity.x;
      }

      prevMousePos = { x: clientX, y: clientY };
    };

    const onPointerUp = () => {
      isDragging = false;
      setIsInteracting(false);
    };

    const domElem = renderer.domElement;
    // Allow vertical scrolling on iOS/mobile while enabling canvas drag
    domElem.style.touchAction = 'pan-y';
    domElem.addEventListener('mousedown', onPointerDown);
    window.addEventListener('mousemove', onPointerMove, { passive: true });
    window.addEventListener('mouseup', onPointerUp);

    domElem.addEventListener('touchstart', onPointerDown, { passive: true });
    window.addEventListener('touchmove', onPointerMove, { passive: true });
    window.addEventListener('touchend', onPointerUp, { passive: true });

    // Dynamic Resize Handler for iOS Safari / Mobile
    const handleResize = () => {
      if (!container) return;
      width = container.clientWidth || window.innerWidth;
      height = container.clientHeight || window.innerHeight;
      const mobileCheck = width < 768;
      const newAspect = width / height;

      basePosY = mobileCheck ? 0.38 : (newAspect < 1.4 ? 0.28 : 0.22);
      camera.aspect = newAspect;
      camera.fov = mobileCheck ? (newAspect < 0.6 ? 56 : 50) : 40;
      camera.position.set(0, mobileCheck ? 0.3 : 0.15, mobileCheck ? 6.2 : 5.4);
      camera.updateProjectionMatrix();

      brainGroup.position.y = basePosY;
      renderer.setSize(width, height);
    };

    window.addEventListener('resize', handleResize);

    // --- ANIMATION & 3D-TO-2D BEACON PROJECTION LOOP ---
    let startTime = performance.now();
    let lastTime = startTime;
    let animationFrameId;

    const vector3Temp = new THREE.Vector3();

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      const now = performance.now();
      const elapsedTime = (now - startTime) * 0.001;
      const delta = Math.min((now - lastTime) * 0.001, 0.1);
      lastTime = now;

      // Inertia & passive orbital drift
      if (!isDragging) {
        rotationVelocity.x *= 0.94;
        rotationVelocity.y *= 0.94;

        brainGroup.rotation.y += rotationVelocity.y + 0.0015;
        brainGroup.rotation.x += rotationVelocity.x;

        // Gentle floating breathing oscillation around framed center
        brainGroup.position.y = basePosY + Math.sin(elapsedTime * 0.8) * 0.035;
      }

      // Dynamic Action Potential Traveling Pulses
      const posAttr = nodeGeometry.attributes.position;
      const sigPosAttr = signalGeometry.attributes.position;
      const sigColAttr = signalGeometry.attributes.color;

      for (let s = 0; s < SIGNAL_COUNT; s++) {
        const sig = signals[s];
        sig.progress += delta * sig.speed;

        if (sig.progress >= 1.0) {
          sig.progress = 0;
          const connIdx = Math.floor(Math.random() * (connections.length / 2)) * 2;
          sig.from = connections[connIdx];
          sig.to = connections[connIdx + 1];
        }

        const pFromX = posAttr.getX(sig.from);
        const pFromY = posAttr.getY(sig.from);
        const pFromZ = posAttr.getZ(sig.from);

        const pToX = posAttr.getX(sig.to);
        const pToY = posAttr.getY(sig.to);
        const pToZ = posAttr.getZ(sig.to);

        const curSignalX = THREE.MathUtils.lerp(pFromX, pToX, sig.progress);
        const curSignalY = THREE.MathUtils.lerp(pFromY, pToY, sig.progress);
        const curSignalZ = THREE.MathUtils.lerp(pFromZ, pToZ, sig.progress);

        sigPosAttr.setXYZ(s, curSignalX, curSignalY, curSignalZ);

        const brightness = Math.sin(sig.progress * Math.PI);
        sigColAttr.setXYZ(
          s,
          sig.color.r * (0.85 + brightness * 0.4),
          sig.color.g * (0.85 + brightness * 0.4),
          sig.color.b * (0.85 + brightness * 0.4)
        );
      }
      sigPosAttr.needsUpdate = true;
      sigColAttr.needsUpdate = true;

      // Project 3D Beacon Positions to Screen Coordinates
      brainGroup.updateMatrixWorld();
      const updatedBeacons = BEACONS_CONFIG.map((b) => {
        vector3Temp.set(b.anchorCoords[0], b.anchorCoords[1], b.anchorCoords[2]);
        vector3Temp.applyMatrix4(brainGroup.matrixWorld);
        vector3Temp.project(camera);

        // Screen mapping with mobile boundary clamping
        const rawX = ((vector3Temp.x + 1) * width) / 2;
        const rawY = ((-vector3Temp.y + 1) * height) / 2;
        const screenX = Math.max(60, Math.min(width - 60, rawX));
        const screenY = Math.max(40, Math.min(height - 70, rawY));
        const isVisible = vector3Temp.z < 1.0; // In front of camera clip

        return {
          ...b,
          x: screenX,
          y: screenY,
          visible: isVisible,
          zDepth: vector3Temp.z
        };
      });

      setBeaconPositions(updatedBeacons);

      renderer.render(scene, camera);
    };

    animate();

    // --- Cleanup ---
    return () => {
      cancelAnimationFrame(animationFrameId);
      domElem.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('mousemove', onPointerMove);
      window.removeEventListener('mouseup', onPointerUp);
      domElem.removeEventListener('touchstart', onPointerDown);
      window.removeEventListener('touchmove', onPointerMove);
      window.removeEventListener('touchend', onPointerUp);
      window.removeEventListener('resize', handleResize);

      lineGeometry.dispose();
      lineMaterial.dispose();
      nodeGeometry.dispose();
      nodeMaterial.dispose();
      diamondTexture.dispose();
      signalGeometry.dispose();
      signalMaterial.dispose();
      renderer.dispose();

      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div 
      ref={containerRef} 
      className={`relative w-full h-full overflow-hidden select-none cursor-grab active:cursor-grabbing ${className}`}
      style={{ minHeight: '560px' }}
      aria-label="Interactive 3D Neural Atlas Brain Visualization"
    >
      {/* Soft Bottom Easing Fade (Only at the very bottom edge) */}
      <div 
        className="absolute inset-0 pointer-events-none z-10"
        style={{
          background: `
            linear-gradient(to bottom, transparent 80%, rgba(5, 4, 9, 0.45) 90%, #050409 100%)
          `
        }}
      />

      {/* Floating 3D Projected Interactive Diamond Beacons */}
      <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden">
        {beaconPositions.map((beacon) => {
          if (!beacon.visible) return null;

          return (
            <button
              key={beacon.id}
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleBeaconClick(beacon.targetId);
              }}
              className="absolute pointer-events-auto transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group transition-transform duration-100 hover:scale-110 focus:outline-none"
              style={{
                left: `${beacon.x}px`,
                top: `${beacon.y}px`,
                opacity: beacon.zDepth > 0.85 ? 0.45 : 1
              }}
              title={`Jump to ${beacon.label}`}
            >
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0d0718]/95 backdrop-blur-xl border border-purple-500/40 text-purple-200 font-mono text-[11px] tracking-wider shadow-[0_0_20px_rgba(192,132,252,0.3)] group-hover:border-purple-400 group-hover:bg-purple-950/80 group-hover:shadow-[0_0_30px_rgba(232,121,249,0.6)] transition-all">
                <span className="w-2 h-2 rotate-45 bg-fuchsia-400 group-hover:bg-cyan-300 shadow-[0_0_8px_#f43f5e] transition-colors" />
                <span className="font-bold">{beacon.label}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default NeuralBrain;
